import { useState, useEffect, useRef, useContext } from 'react';

import ContextMenu from './ContextMenu';
import SliceInfoPopup from './SliceInfoPopup';
import { ImageSettingContext } from './Controls';
import { MarkerContext } from './SliceMarker';

import './style.css';
import SliceMarker from './SliceMarker';
import worker from './sharedWorker';





/* 
  Get pixel scaling params for windowing.
  Window params are either from presets or from the slice metadata
*/
function getPixelScaling(windowParams){
  const minX = windowParams.windowCenter - windowParams.windowWidth / 2;
  const maxX = windowParams.windowCenter + windowParams.windowWidth / 2;
  const scale = (maxX - minX) / 255 || 1;
  return {minX, maxX, scale};  
}


function adjustCanvasParent(canvas){
  if (!canvas) return;

  // Resize parent quadrant element (if present) to fit the canvas plus padding/borders
  try {
    let el = canvas.parentElement;
    while (el && !el.classList.contains('quadrants')) el = el.parentElement;
    if (el) {
      const paddingOffset = 10; // extra space you want around canvas (px)

      const cs = getComputedStyle(el);
      const box = cs.boxSizing; // 'content-box' or 'border-box'

      const padBorderW = (parseFloat(cs.paddingLeft || 0) + parseFloat(cs.paddingRight || 0)
                       + parseFloat(cs.borderLeftWidth || 0) + parseFloat(cs.borderRightWidth || 0)) || 0;
      const padBorderH = (parseFloat(cs.paddingTop || 0) + parseFloat(cs.paddingBottom || 0)
                       + parseFloat(cs.borderTopWidth || 0) + parseFloat(cs.borderBottomWidth || 0)) || 0;

      const marginW = (parseFloat(cs.marginLeft || 0) + parseFloat(cs.marginRight || 0)) || 0;
      const marginH = (parseFloat(cs.marginTop || 0) + parseFloat(cs.marginBottom || 0)) || 0;

      // Include padding/border and margins in the computed CSS size so outer size increases accordingly
      let cssWidth;
      let cssHeight;
      if (box === 'border-box') {
        // border-box: width includes padding+border; add margin to make outer size larger
        cssWidth = Math.max(0, canvas.width + paddingOffset + padBorderW + marginW);
        cssHeight = Math.max(0, canvas.height + paddingOffset + padBorderH + marginH);
      } else {
        // content-box: width is content only; add padding/border and margin to achieve larger outer size
        cssWidth = Math.max(0, canvas.width + paddingOffset + padBorderW + marginW);
        cssHeight = Math.max(0, canvas.height + paddingOffset + padBorderH + marginH);
      }

      el.style.width = Math.round(cssWidth) + 'px';
      el.style.height = Math.round(cssHeight) + 'px';
    }
  } catch (e) {
    // ignore
  }
}

// Main slice viewer component for displaying DICOM slices in different planes
export default function SliceViewer({ mode}) {
  
  const [sliceIndex, setSliceIndex] = useState(null); // Current slice index for this view
  const [showInfo, setShowInfo] = useState(false); // Show slice info popup
  const [pixelScaling, setPixelScaling] = useState(null);
  const [cursorPos, setCursorPos] = useState([0, 0]);
  const [volumeInfo, setVolumeInfo] = useState(null);
  const [window, setWindow] = useState(null);

  const viewerRef = useRef(null);
  const sliceCanvasRef = useRef(null); // Canvas for slice image
  const sliceIndexRef = useRef(sliceIndex); // Ref to keep sliceIndex in event handlers

  const imageContext = useContext(ImageSettingContext) ;
  const imageSettings = imageContext.settings ;
  const updateImageSettings = imageContext.updateSettings;

  const markerContext = useContext(MarkerContext);
  const updateMarkers = markerContext.updateMarkers;

  // Context menu items
  const menuItems = [
    { label: 'Slice Info', onClick: () => setShowInfo(true) }
  ];

  
  // Updates cursor position in real-world coordinates based on mouse event
  function updateCursorPosition(event) {

    if (!volumeInfo) return;
 
    const sliceNo = sliceIndexRef.current;
    const canvas = sliceCanvasRef.current;
    const bb = canvas.getBoundingClientRect();

    let mouseX, mouseY;
    
    if (event) {
      mouseX = event.clientX;
      mouseY = event.clientY;
      setCursorPos([mouseX, mouseY]);
    } else {
      mouseX = cursorPos[0];
      mouseY = cursorPos[1];
    }
    // Convert mouse position to canvas pixel coordinates
    const canvasX = Math.max(0, Math.min(canvas.width - 1, Math.floor((mouseX - bb.left) / bb.width * canvas.width)));
    const canvasY = Math.max(0, Math.min(canvas.height - 1, Math.floor((mouseY - bb.top) / bb.height * canvas.height)));

    // Don't send to worker on every mousemove - causes performance issues
    worker.postMessage({ action: "updateCursorPosition", payload:{ mode, sliceNo, canvasX, canvasY }});
  }



  useEffect(() => {

    function onNewSliceImage(payload) {
      const { mode: rxdMode, numRows, numCols, buffer } = payload;

      if (rxdMode !== mode) return;

      const canvas = sliceCanvasRef.current;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;

      if (canvas.width !== numCols || canvas.height !== numRows) {
        canvas.width = numCols;
        canvas.height = numRows;
        adjustCanvasParent(canvas);

        // Update the markers canvas size as well
        updateMarkers({ [mode]: { canvasWidth: numCols, canvasHeight: numRows } });
      }

      // Convert ArrayBuffer from worker to ImageData
      const imageData = new ImageData(new Uint8ClampedArray(buffer), numCols, numRows);
      ctx.putImageData(imageData, 0, 0);
    }

    const workerHandler = (e) => {
      const { type, payload } = e.data;
      switch (type) {
        case "newVolume":
          const { volumeInfo:vol, window:win } = payload;
          setWindow(win);
          setVolumeInfo(vol);
          break;
        case "sliceImage":
          onNewSliceImage(payload);
          break;
      }

    }

    worker.addEventListener("message", workerHandler);


    return () => { 
      worker.removeEventListener("message", workerHandler);
    }

  }, []);



   // Recalculate pixel sclaing when window preset changes.
  useEffect(() => {
    
    if (imageSettings.window.preset === 'DICOM'){
      setPixelScaling(null)

      if (!window) return;
      console.log(window);
      updateImageSettings({ window: {preset: 'DICOM', center: Math.round(window.center), width: Math.round(window.width)} });

    } else if (imageSettings.window.preset !== 'Custom'){
      let win = getPixelScaling({windowWidth: imageSettings.window.width, windowCenter: imageSettings.window.center});
      setPixelScaling(win);      
    }

  }, [imageSettings.window.preset]);

  // Recalculate pixel sclaing when changes in windowing.
  useEffect(() => {
    if (imageSettings.window.preset === 'Custom'){
      let win = getPixelScaling({windowWidth: imageSettings.window.width, windowCenter: imageSettings.window.center});
      setPixelScaling(win);
    }
  }, [imageSettings.window.width, imageSettings.window.center]);
    
  // Re-draw slice if the pixel scaling changes
  useEffect(() => {
    worker.postMessage({ action: "drawSliceImage", payload:{mode, sliceIndex, pixelScaling}});
  }, [pixelScaling])


  // Draws the current slice image when sliceIndex changes
  useEffect(() => {
    sliceIndexRef.current = sliceIndex;
    if (sliceIndex !== null) {

      worker.postMessage({ action: "drawSliceImage", payload:{mode, sliceIndex, pixelScaling}});
      updateCursorPosition(null);
    }
  }, [sliceIndex]);




  // When new volume is loaded.
  useEffect(() => {

    
    const canvas = sliceCanvasRef.current;

    // Set scroll handler
    const handleWheel = (event) => {
      event.preventDefault();
      const current = sliceIndexRef.current;
      let maxIndex;
      if (mode === "axial") {
        maxIndex = volumeInfo.zCount - 1;
      } else if (mode === "sagittal") {
        maxIndex = volumeInfo.xCount - 1;
      } else if (mode === "coronal") {
        maxIndex = volumeInfo.yCount - 1;
      }
      let si = event.deltaY > 0 ? Math.min(current + 1, maxIndex) : Math.max(current - 1, 0);
      setSliceIndex(si);

      let marker_pos = mode === "axial" ? maxIndex - si : si;
      updateMarkers({ [mode]: { position: marker_pos } });
    };

    // Throttle mousemove with trailing edge to capture final resting position
    let lastMouseMoveTime = 0;
    let lastPendingEvent = null;
    let timeoutId = null;
    const throttleDelay = 250; // milliseconds
    
    // Set mouse handler with throttling
    const handleMouseMove = (event) => {
      const now = Date.now();
      lastPendingEvent = event;
      
      if (now - lastMouseMoveTime >= throttleDelay) {
        lastMouseMoveTime = now;
        updateCursorPosition(event);
        
        // Clear any pending timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      } else {
        // Schedule trailing edge update if not already scheduled
        if (!timeoutId) {
          timeoutId = setTimeout(() => {
            if (lastPendingEvent) {
              updateCursorPosition(lastPendingEvent);
            }
            timeoutId = null;
            lastPendingEvent = null;
          }, throttleDelay - (now - lastMouseMoveTime));
        }
      }
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('mousemove', handleMouseMove, { passive: false });

    // Set intial slice after loading new volume
    if (volumeInfo) {
      let initial;
      if (mode === "axial") {
        initial = Math.floor(volumeInfo.zCount / 2);
      } else if (mode === "sagittal") {
        initial = Math.floor(volumeInfo.xCount / 2);
      } else if (mode === "coronal") {
        initial = Math.floor(volumeInfo.yCount / 2);
      }
      setSliceIndex(initial);
      updateMarkers({ [mode]: { position: initial } });
    }    

    return () => {
      canvas.removeEventListener('wheel', handleWheel, { passive: false });
      canvas.removeEventListener('mousemove', handleMouseMove, { passive: false });
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    
  }, [volumeInfo])  


  // Render viewer UI: context menu, coordinates, slice canvas, markers, info popup
  return (
      <div ref={viewerRef}>
        <ContextMenu menuItems={menuItems} targetRef={viewerRef}/>
        <div className="cursor-info-box">
          { volumeInfo && <InfoAtCursor mode={mode} /> }
        </div>
        <canvas ref={sliceCanvasRef} className="sliceCanvas"></canvas>
        <SliceMarker mode={mode}/>
        <SliceInfoPopup
          open={showInfo}
          onClose={() => setShowInfo(false)}
          numSlices={0}
          metadata={null}
          viewName={mode}
        />
      </div>
  );
}

// Displays cursor coordinates in real-world space, frame info, Hounsfield Units
function InfoAtCursor({ mode }) {
  const [coords, setCoords] = useState([0, 0, 0]);
  const [value, setValue] = useState(null)
  const [frameInfo, setFrameInfo] = useState({frameNo: 0, frameTotal: 0})


  const workerHandler = (e) => {
    const { type, payload } = e.data;
      if (type === "newCursonInfo"){
        const { viewMode, position, valueAtPos, frameVal } = payload;

        if ( !viewMode || viewMode !== mode ) return;

        //setCoords(position);
        setCoords([position[0].toFixed(1), position[1].toFixed(1), position[2].toFixed(1)]);
        setValue(valueAtPos);
        setFrameInfo(prev => ({...prev, ...frameVal}))
      }

  }

  useEffect(() => {
    worker.addEventListener("message", workerHandler);
    return () => {
      worker.removeEventListener("message", workerHandler);
    }
  }, [])

  return (
    <div>
      <div>X: {coords[0]}</div>
      <div>Y: {coords[1]}</div>
      <div>Z: {coords[2]}</div>
      <div>HU: {value}</div>
      <div>Frame {frameInfo.frameNo} / {frameInfo.frameTotal}</div>
    </div>
  );
}

